import React, { useState, forwardRef } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { InView } from 'react-intersection-observer';

import useWindowSize from '~/hooks/useWindowSize';

import { formatText, buildThresholdList } from '~/utilities/helpers';

import { h2, h4 } from '~/styles/typography';
import { calcVw } from '~/styles/helpers';
import { kebabCase, uniqueId } from 'lodash';

const Slide = styled.div`
    width: calc(${calcVw(100)} - 2 * ${({ theme }) => theme.layout.gutter});
    margin-right: ${({ theme }) => theme.layout.gutter};
    display: flex;
    flex-direction: column;
    justify-content: center;
    opacity: ${({ opacity }) => opacity};
    ${({ isSticky }) => (!isSticky ? 'opacity: 1 !important;' : '')};

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        width: auto;
        margin-right: 0;
        height: 100vh;
    }
`;

const SlideTitle = styled.h3`
    ${h2};
    color: ${({ theme }) => theme.colors.primary.white};
`;

const SlideDescription = styled.div`
    ${h4};
    color: ${({ theme }) => theme.colors.primary.white};
    margin-top: 30px;

    p:not(:last-of-type) {
        margin-bottom: 20px;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        margin-top: 45px;
    }
`;

const FeaturesCarouselTextSliderSlide = forwardRef(
    (
        {
            title,
            description,
            featuresCarouselState,
            featuresCarouselDispatch,
            slideIndex
        },
        ref
    ) => {
        const [opacity, setOpacity] = useState(0);
        const sizes = useWindowSize();
        const onChangeInView = (inView, entry, index) => {
            if (ref.current) {
                if (
                    entry.intersectionRatio > 0.5 &&
                    index !== featuresCarouselState.currentSlide
                ) {
                    featuresCarouselDispatch({
                        type: 'CHANGE_SLIDE',
                        slideIndex: index
                    });
                }
                if (sizes.includes('tablet')) {
                    setOpacity(entry.intersectionRatio);
                }
            }
        };

        return (
            <InView
                threshold={buildThresholdList()}
                rootMargin="0px 0px 0px 0px"
                as="div"
                key={uniqueId(kebabCase(title))}
                onChange={(inView, entry) =>
                    onChangeInView(inView, entry, slideIndex)
                }
            >
                <Slide
                    {...{ ref, opacity }}
                    isSticky={featuresCarouselState.isSticky}
                >
                    <SlideTitle>{formatText(title)}</SlideTitle>
                    {description && (
                        <SlideDescription>
                            {formatText(description)}
                        </SlideDescription>
                    )}
                </Slide>
            </InView>
        );
    }
);

FeaturesCarouselTextSliderSlide.propTypes = {
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    featuresCarouselState: PropTypes.shape({}).isRequired,
    featuresCarouselDispatch: PropTypes.func.isRequired,
    opacity: PropTypes.number
};

export default FeaturesCarouselTextSliderSlide;
